// src/ui/ChatLockGate.jsx
//
// 🔒 A LOCKED CHAT, ON THE WEB — its lock is drawn instead of the conversation (no messages read,
//    nothing marked read, no receipt sent) until its PIN is right; then the conversation mounts and
//    is held open while it is on screen. The PIN is the one set on the phone (services/chatLock).

import { useEffect, useRef, useState } from 'react';
import { useChatLock, holdChatOpen, verifyChatPin, chatLockLen, chatLockWait } from '../services/chatLock';

const fmt = (ms) => { const s = Math.max(1, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

function LockPane({ chat, onBack }) {
  const id = chat?.id;
  const len = chatLockLen(id);
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [wait, setWait] = useState(() => chatLockWait(id));
  const [shake, setShake] = useState(0);
  const input = useRef(null);
  useEffect(() => { const t = setTimeout(() => input.current && input.current.focus(), 50); return () => clearTimeout(t); }, [id]);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setInterval(() => { const w = chatLockWait(id); setWait(w); if (w <= 0) clearInterval(t); }, 500);
    return () => clearInterval(t);
  }, [wait > 0, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const put = (v) => {
    const next = String(v).replace(/\D/g, '').slice(0, len);
    setPin(next); setErr('');
    if (next.length < len) return;
    setTimeout(() => {
      const r = verifyChatPin(id, next);
      if (r.ok) return;
      setPin(''); setShake((n) => n + 1);
      if (r.waitMs > 0) setWait(r.waitMs);
      else setErr(r.left > 0 && r.left <= 3 ? `Wrong PIN · attempts left: ${r.left}` : 'Wrong PIN');
    }, 80);
  };
  const name = chat?.isGroup ? (chat?.name || chat?.groupName || 'Group') : (chat?.otherUser?.fullName || chat?.title || '');
  return (
    <div className="chat-lock-pane" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, background: 'var(--bg)', color: 'var(--text)' }}
      onClick={() => input.current && input.current.focus()}>
      {onBack && <button type="button" onClick={onBack} aria-label="Back" style={{ position: 'absolute', top: 14, left: 14, background: 'none', border: 0, color: 'var(--text)', fontSize: 22, cursor: 'pointer' }}>‹</button>}
      <div style={{ width: 72, height: 72, borderRadius: 36, background: '#30A46C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }} aria-hidden>🔒</div>
      {name ? <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.75 }}>{name}</div> : null}
      <div style={{ fontSize: 22, fontWeight: 700 }}>Enter PIN to open</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320 }}>This chat is locked on your phone. Its PIN opens it here too.</div>
      <div key={shake} className={shake ? 'lock-shake' : ''} style={{ display: 'flex', gap: 16, margin: '10px 0', animation: shake ? 'lockshake 0.3s' : undefined }}>
        {Array.from({ length: len }).map((_, i) => (
          <span key={i} style={{ width: 14, height: 14, borderRadius: 7, border: '1.5px solid', borderColor: i < pin.length ? '#30A46C' : 'var(--text-2)', background: i < pin.length ? '#30A46C' : 'transparent' }} />
        ))}
      </div>
      <input ref={input} value={pin} onChange={(e) => put(e.target.value)} disabled={wait > 0}
        type="password" inputMode="numeric" autoComplete="off" aria-label="PIN" maxLength={len}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 1, height: 1 }} />
      <div style={{ minHeight: 20, fontSize: 14, color: wait > 0 ? 'var(--text-2)' : 'var(--danger)' }} aria-live="polite">
        {wait > 0 ? `Too many attempts. Try again in ${fmt(wait)}` : err}
      </div>
      <style>{'@keyframes lockshake{0%,100%{transform:translateX(0)}20%{transform:translateX(-9px)}40%{transform:translateX(9px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}'}</style>
    </div>
  );
}

export default function ChatLockGate({ chat, onBack, children }) {
  const id = chat?.id;
  const { needs } = useChatLock(id);
  useEffect(() => (needs || !id ? undefined : holdChatOpen(id)), [needs, id]);
  if (needs) return <LockPane chat={chat} onBack={onBack} />;
  return children;
}
